import React, { useMemo } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isBetween from "dayjs/plugin/isBetween";
import "dayjs/locale/es";
import ImageExample from "../../../assets/img/401.png";
import { useNavigate } from "react-router-dom";
import { FiMapPin, FiClock, FiUser, FiArrowRight } from "react-icons/fi";

dayjs.extend(relativeTime);
dayjs.extend(isBetween);
dayjs.locale("es");

const fmt = (n) => {
  if (!n) return null;
  return new Intl.NumberFormat("es-CL", { minimumFractionDigits: 0 }).format(n);
};

const splitName = (full) => {
  const rx = /((?:Lu|Ma|Mi|Ju|Vi|Sa|Do)(?:-(?:Lu|Ma|Mi|Ju|Vi|Sa|Do))*\s+\d{1,2}:\d{2}\s*h?\.?)/i;
  const m = full.match(rx);
  return m
    ? { nombre: full.replace(rx, "").trim(), horario: m[0].trim() }
    : { nombre: full, horario: "" };
};

/* Accent color por tipo de actividad */
const ACCENT = {
  Fútbol:          "#0047bb",
  Natación:        "#0284c7",
  Gimnasia:        "#7c3aed",
  Tenis:           "#d97706",
  "Artes Marciales": "#dc2626",
  Baile:           "#db2777",
  Yoga:            "#6d28d9",
  Running:         "#0047bb",
  Musculación:     "#b45309",
  default:         "#0047bb",
};
const accent = (tipo) => ACCENT[tipo] || ACCENT.default;

export default function TallerCard({ taller }) {
  const navigate = useNavigate();
  const { nombre, horario } = useMemo(() => splitName(taller.nombre), [taller.nombre]);

  /* Cupos */
  const ilimitado = taller.horarios.some((h) => h.cuposIlimitados);
  const total     = ilimitado ? Infinity : taller.horarios.reduce((s, h) => s + (h.cupos || 0), 0);
  const ocupados  = taller.horarios.reduce((s, h) => s + (h.cuposOcupados || 0) + (h.cuposReservados || 0), 0);
  const disponibles = ilimitado ? Infinity : total - ocupados;
  const pct = total > 0 && !ilimitado ? Math.min(Math.round((ocupados / total) * 100), 100) : 0;

  /* Precios */
  const precioMin = useMemo(() => {
    if (!taller.modalidadPago?.length) return null;
    const vals = taller.modalidadPago.map(m => m.valorParticular || 0).filter(Boolean);
    return vals.length ? Math.min(...vals) : null;
  }, [taller.modalidadPago]);
  const precioVecino = useMemo(() => {
    if (!taller.modalidadPago?.length) return null;
    const vals = taller.modalidadPago.map(m => m.valorVecino || 0).filter(Boolean);
    return vals.length ? Math.min(...vals) : null;
  }, [taller.modalidadPago]);

  const esGratis = taller.esGratuito || !precioMin;

  /* Estado inscripción */
  const abierto = taller.inscripcionesEstado === "abiertas" && taller.estado === "activo";
  const sinVentana = !taller.inscripcionInicio && !taller.inscripcionFin;
  const enPeriodo = useMemo(() => {
    if (sinVentana) return abierto;
    const now = dayjs(), ini = taller.inscripcionInicio ? dayjs(taller.inscripcionInicio) : null, fin = taller.inscripcionFin ? dayjs(taller.inscripcionFin) : null;
    if (ini && fin) return now.isBetween(ini, fin, null, "[]");
    if (ini) return now.isAfter(ini);
    if (fin) return now.isBefore(fin);
    return false;
  }, [taller.inscripcionInicio, taller.inscripcionFin, sinVentana, abierto]);

  const puedeInscribirse = abierto && enPeriodo && (ilimitado || disponibles > 0);
  const sinCupos = !ilimitado && disponibles <= 0;
  const pocoCupo = !ilimitado && disponibles > 0 && disponibles <= 5;

  /* Edades */
  const edades = useMemo(() => {
    const { edadMin: mn, edadMax: mx } = taller;
    if (!mn && !mx) return "Todas las edades";
    if (mn && mx) return `${mn} – ${mx} años`;
    if (mn) return `+${mn} años`;
    return `Hasta ${mx} años`;
  }, [taller.edadMin, taller.edadMax]);

  const ac = accent(taller.tipoActividad);
  const barColor = pct >= 90 ? "#ef4444" : pct >= 65 ? "#f97316" : "#0047bb";

  return (
    <article
      className="taller-card nd-card flex flex-col overflow-hidden"
      onClick={() => navigate(`/talleres/${taller._id}`)}
      style={{ borderRadius: 20 }}
    >
      {/* ── Imagen ── */}
      <div className="relative overflow-hidden" style={{ height: 176 }}>
        <img
          src={taller.imagen?.url || ImageExample}
          alt={nombre}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {/* gradient bottom */}
        <div className="absolute inset-0" style={{
          background: "linear-gradient(to top, rgba(0,0,0,.55) 0%, transparent 55%)"
        }} />

        {/* Tipo actividad */}
        <span className="absolute top-3 left-3 text-[11px] font-bold px-2.5 py-1 rounded-full text-white"
          style={{ background: ac }}>
          {taller.tipoActividad}
        </span>

        {/* Precio badge */}
        <span className={`absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full
          ${esGratis ? "badge-gratis" : "bg-white/90 text-gray-900"}`}>
          {esGratis ? "¡Gratis!" : `Desde $${fmt(precioVecino || precioMin)}`}
        </span>

        {/* Sin cupos */}
        {sinCupos && (
          <span className="absolute bottom-3 left-3 text-[11px] font-bold px-2.5 py-1 rounded-full bg-red-500 text-white">
            Sin cupos
          </span>
        )}
        {pocoCupo && (
          <span className="absolute bottom-3 left-3 text-[11px] font-bold px-2.5 py-1 rounded-full bg-orange-500 text-white">
            ¡Últimos {disponibles}!
          </span>
        )}
      </div>

      {/* ── Contenido ── */}
      <div className="flex flex-col flex-1 p-4 gap-3">

        {/* Título */}
        <h3 className="font-bold text-gray-900 text-[15px] leading-snug line-clamp-2">{nombre}</h3>

        {/* Meta */}
        <div className="space-y-1.5">
          {taller.recinto?.nombre && (
            <div className="flex items-center gap-1.5 text-[12px] text-gray-500">
              <FiMapPin size={13} className="flex-shrink-0 text-blue-500" />
              <span className="truncate">{taller.recinto.nombre}</span>
            </div>
          )}
          {horario && (
            <div className="flex items-center gap-1.5 text-[12px] text-gray-500">
              <FiClock size={13} className="flex-shrink-0 text-blue-500" />
              <span className="truncate">{horario}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-[12px] text-gray-500">
            <FiUser size={13} className="flex-shrink-0 text-blue-500" />
            <span>{edades}</span>
          </div>
        </div>

        {/* Barra cupos */}
        {!ilimitado && total > 0 && (
          <div>
            <div className="cupos-bar">
              <div className="cupos-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
            </div>
            <p className="text-[11px] mt-1" style={{ color: barColor }}>
              {sinCupos ? "Sin cupos" : pocoCupo ? `¡Solo ${disponibles} cupos!` : `${disponibles} cupos disponibles`}
            </p>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Footer: precio + CTA */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100 gap-2">
          <div className="min-w-0">
            {esGratis ? (
              <p className="text-sm font-bold text-blue-600">Gratuito</p>
            ) : (
              <div>
                {precioVecino && (
                  <p className="text-[11px] text-gray-400 leading-none">Vecino</p>
                )}
                <p className="text-sm font-bold text-gray-900">
                  ${fmt(precioVecino || precioMin)}
                  <span className="text-[10px] font-normal text-gray-400"> /mes</span>
                </p>
              </div>
            )}
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/talleres/${taller._id}`); }}
            className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold transition-all active:scale-95"
            style={puedeInscribirse
              ? { background: "#0047bb", color: "#fff" }
              : { background: "#f3f4f6", color: "#374151" }
            }
          >
            {puedeInscribirse && esGratis ? "Inscribirse" : puedeInscribirse ? "Ver taller" : "Ver detalles"}
            <FiArrowRight size={13} />
          </button>
        </div>
      </div>
    </article>
  );
}
